import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchTasksByMapTaskId,
  TaskStatus,
  mapTaskToWorkflow,
  unmapTaskFromWorkflow,
  updateTask,
  createTask,
  deleteTask,
  updateTaskProgress,
} from '../../store/slices/taskSlice';
import { fetchChannels, fetchDirectMessages } from '../../store/slices/channelSlice';
import { fetchUsers } from '../../store/slices/userSlice';
import { addNotification } from '../../store/slices/uiSlice';
import { sendMessage, assignWorkflowMessage, addReaction, addMessage, removeMessage } from '../../store/slices/chatSlice';
import { resolveLocalMessage } from '../../realm/offlineLookups';
import PresignedAvatar from '../common/PresignedAvatar';
import ImageViewerModal from '../chat/ImageViewerModal';
import ChatMessageBubblePreview from '../chat/ChatMessageBubblePreview';
import AttachmentGroupBar from '../chat/AttachmentGroupBar';
import TaskProgressControl, { normalizeTaskProgress } from './TaskProgressControl';
import SpacePicker from './SpacePicker';
import TaskSubtaskItem, { TaskEstimateBadge } from './TaskSubtaskItem';
import QuickTaskComposer from './QuickTaskComposer';
import EnableLiveTrackingButton from '../liveTracking/EnableLiveTrackingButton';
import WorkItemTimer from '../common/WorkItemTimer';
import WorkItemShareMenu from '../common/WorkItemShareMenu';
import { WORK_ITEM_SHARE_KIND } from '../../utils/workItemShare';
import { PlusIcon } from '../chat/SidebarIcons';
import { formatEstimateTime } from '../../utils/estimateTime';
import PostCompletionEditor from '../templates/PostCompletionEditor';
import WorkCloseProgressOverlay from '../common/WorkCloseProgressOverlay';
import { isWorkflowMessage, messageToWorkflowItem, taskWorkflowMessageIds, taskDelegateIds, formatTaskAssigneeLabel, workflowItemLabel, unfinishedSubtasksError, unfinishedSubtasksMessage, apiErrorMessage, explicitWorkflowMessageIds } from '../../utils/workflowTaskUtils';
import {
  getActiveWorkflowByCategory,
  getReactionDisplay,
  applyOptimisticReaction,
  isWorkflowReaction,
  resolveUrgentWorkflowReactionId,
  WORKFLOW_URGENT_ID,
  messageHasUrgentWorkflow,
} from '../../utils/reactionConfig';
import ReactionPicker from '../chat/ReactionPicker';
import {
  mergeComments,
  commentKey,
  commentTime,
  collectThreadReplies,
  removeCommentById,
} from '../../utils/threadCommentUtils';
import { downloadAttachment, openAttachmentFallback } from '../../utils/fileUtils';
import axios from 'axios';
import { API_URL } from '../../config/api';
import WorkflowSearchDialog from './WorkflowSearchDialog';
import WorkflowAssigneeDropdown from '../chat/WorkflowAssigneeDropdown';
import { useCompletionTimeDialog } from '../chat/CompletionTimeDialog';
import { useUrgentDeadlineDialog } from '../chat/UrgentDeadlineDialog';
import UrgentCountdownBadge from '../chat/UrgentCountdownBadge';
import CompactRichEditor from '../common/CompactRichEditor';
import MessageMentionContent from '../common/MessageMentionContent';
import MessageInput from '../chat/MessageInput';
import WorkItemHistoryPanel from '../common/WorkItemHistoryPanel';
import { parseUrgentDeadline } from '../../utils/urgentDeadlineUtils';
import TaskReminderModal from './TaskReminderModal';

const CompleteTaskIcon = ({ className = 'w-3.5 h-3.5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const DeleteTaskIcon = ({ className = 'w-3.5 h-3.5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const isNamedSubtask = (item) => Boolean(
  item
  && (item.title || '').trim()
  && !(item.messageId && String(item.messageId).trim()),
);

const isSubtaskFinished = (item) => {
  const minutes = Number(item?.completionTimeInMinutes);
  return Number.isFinite(minutes) && minutes > 0;
};

const PICKER_MAX_WIDTH = 360;
const PICKER_ESTIMATED_HEIGHT = 360;
const PICKER_GAP = 8;
const REACTION_PICKER_Z_INDEX = 1300;

const computeReactionPickerPosition = (anchorEl) => {
  if (!anchorEl) return null;
  const rect = anchorEl.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let top = rect.bottom + PICKER_GAP;
  let left = rect.left;
  if (left + PICKER_MAX_WIDTH > vw) left = Math.max(8, vw - PICKER_MAX_WIDTH - 8);
  if (left < 8) left = 8;
  if (top + PICKER_ESTIMATED_HEIGHT > vh) {
    top = rect.top - PICKER_ESTIMATED_HEIGHT - PICKER_GAP;
  }
  if (top < 8) top = 8;
  return { top, left };
};

const sameUserId = (a, b) => {
  if (a == null || b == null) return false;
  return String(a).toLowerCase() === String(b).toLowerCase();
};

const looksLikeHtml = (value) => {
  const v = String(value || '');
  return /<[a-z][\s\S]*?>/i.test(v) || /&lt;\/?[a-z]/i.test(v);
};

const normalizeDescriptionHtml = (value) => {
  let html = String(value || '');
  if (/&lt;\/?[a-z]/i.test(html)) {
    html = html
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&amp;/g, '&');
  }
  return html;
};

const getInitialWordsFromDescription = (description, maxWords = 12) => {
  if (!description) return 'No description';

  let text = '';
  if (looksLikeHtml(description)) {
    const tmp = document.createElement('div');
    tmp.innerHTML = normalizeDescriptionHtml(description);
    text = tmp.textContent || tmp.innerText || '';
  } else {
    text = description;
  }

  const words = text.trim().split(/\s+/).filter((word) => word.length > 0);
  if (words.length === 0) return 'No description';
  const initialWords = words.slice(0, maxWords).join(' ');
  return words.length > maxWords ? `${initialWords}...` : initialWords;
};

const getStatusColor = (status) => {
  switch (status) {
    case TaskStatus.PENDING:
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    case TaskStatus.PICK_TODAY:
      return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200';
    case TaskStatus.IN_PROGRESS:
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    case TaskStatus.COMPLETED:
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    case TaskStatus.CANCELLED:
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  }
};

const getStatusLabel = (status) => {
  switch (status) {
    case TaskStatus.PENDING:
      return 'Pending';
    case TaskStatus.PICK_TODAY:
      return 'Pick today';
    case TaskStatus.IN_PROGRESS:
      return 'In Progress';
    case TaskStatus.COMPLETED:
      return 'Completed';
    case TaskStatus.CANCELLED:
      return 'Cancelled';
    default:
      return status || 'Pending';
  }
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
};

const formatRelativeTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffTime = now - date;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return 'Today';
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} Days ago`;
  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return weeks === 1 ? '1 Week ago' : `${weeks} Weeks ago`;
  }
  if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return months === 1 ? '1 Month ago' : `${months} Months ago`;
  }
  const years = Math.floor(diffDays / 365);
  return years === 1 ? '1 Year ago' : `${years} Years ago`;
};

const hasTaskDescription = (item) => {
  const desc = (item?.description || '').trim();
  if (!desc) return false;
  if (desc === '<p><br></p>' || desc === '<p></p>') return false;
  return true;
};

const getMessageDisplayAttachments = (message) => {
  if (!message) return [];
  if (Array.isArray(message.timelineAttachments) && message.timelineAttachments.length > 0) {
    return message.timelineAttachments;
  }
  return Array.isArray(message.attachments) ? message.attachments : [];
};

const renderRichOrPlain = (value, emptyText) => {
  if (!value || !String(value).trim()) {
    return <p className="whitespace-pre-wrap">{emptyText}</p>;
  }
  if (looksLikeHtml(value)) {
    return (
      <MessageMentionContent
        isRichText
        content={normalizeDescriptionHtml(value)}
        mentionOnLight
        className="ql-editor-content [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-md"
        style={{ fontSize: '0.875rem', lineHeight: '1.7' }}
      />
    );
  }
  return (
    <MessageMentionContent
      content={value}
      mentionOnLight
      className="whitespace-pre-wrap text-sm"
    />
  );
};

const DROPDOWN_MAX_HEIGHT = 220;

const TaskDelegateDropdown = ({
  users = [],
  selectedIds = [],
  selectedLabel = '',
  isOpen = false,
  searchQuery = '',
  disabled = false,
  onToggleOpen,
  onSearchChange,
  onToggleUser,
}) => {
  const searchInputRef = useRef(null);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const [panelPosition, setPanelPosition] = useState(null);

  const filteredUsers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return users;
    return users.filter((person) => person.label.toLowerCase().includes(query));
  }, [users, searchQuery]);

  const updatePanelPosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const openAbove = spaceBelow < DROPDOWN_MAX_HEIGHT && rect.top > DROPDOWN_MAX_HEIGHT;
    setPanelPosition({
      top: openAbove ? rect.top - 4 : rect.bottom + 4,
      left: rect.left,
      width: Math.max(rect.width, 220),
      openAbove,
    });
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setPanelPosition(null);
      return undefined;
    }
    updatePanelPosition();
    const timer = setTimeout(() => searchInputRef.current?.focus(), 0);
    window.addEventListener('scroll', updatePanelPosition, true);
    window.addEventListener('resize', updatePanelPosition);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', updatePanelPosition, true);
      window.removeEventListener('resize', updatePanelPosition);
    };
  }, [isOpen, updatePanelPosition]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (event) => {
      if (
        containerRef.current?.contains(event.target)
        || panelRef.current?.contains(event.target)
      ) {
        return;
      }
      onToggleOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onToggleOpen]);

  const dropdownPanel = isOpen && panelPosition ? (
    <div
      ref={panelRef}
      className="rounded-lg border overflow-hidden bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white shadow-lg"
      style={{
        position: 'fixed',
        top: panelPosition.top,
        left: panelPosition.left,
        width: panelPosition.width,
        transform: panelPosition.openAbove ? 'translateY(-100%)' : undefined,
        zIndex: 1100,
      }}
    >
      <div className="p-2 border-b border-gray-200 dark:border-gray-700">
        <input
          ref={searchInputRef}
          type="text"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search people…"
          className="w-full h-7 px-2 text-xs rounded-md border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/50 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500/50"
        />
      </div>
      <div className="max-h-40 overflow-y-auto py-1">
        {users.length === 0 ? (
          <div className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">No users available</div>
        ) : filteredUsers.length === 0 ? (
          <div className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">No users match your search</div>
        ) : (
          filteredUsers.map((person) => {
            const isSelected = selectedIds.some((id) => String(id) === String(person.id));
            return (
              <button
                key={person.id}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onToggleUser(person.id);
                }}
                className={`w-full flex items-center gap-2 px-3 py-2 text-xs text-left transition-colors ${
                  isSelected
                    ? 'bg-violet-50 dark:bg-violet-900/30 text-violet-800 dark:text-violet-200'
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <span className={`w-3.5 h-3.5 flex-shrink-0 rounded border ${
                  isSelected
                    ? 'bg-primary-500 border-primary-500'
                    : 'border-gray-300 dark:border-gray-500'
                }`}>
                  {isSelected ? (
                    <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : null}
                </span>
                <span className="truncate flex-1">{person.label}</span>
              </button>
            );
          })
        )}
      </div>
    </div>
  ) : null;

  return (
    <div ref={containerRef} className={`w-full relative ${isOpen ? 'z-[200]' : ''}`}>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => !disabled && onToggleOpen(!isOpen)}
        className="w-full flex items-center gap-2 min-h-8 px-2.5 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm hover:border-gray-400 dark:hover:border-gray-500 disabled:opacity-60 disabled:cursor-not-allowed"
        aria-expanded={isOpen}
      >
        <span className={`truncate flex-1 text-left ${selectedLabel ? '' : 'text-gray-400 dark:text-gray-500'}`}>
          {selectedLabel || 'Select people'}
        </span>
        <svg className="w-4 h-4 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {typeof document !== 'undefined' && dropdownPanel
        ? ReactDOM.createPortal(dropdownPanel, document.body)
        : dropdownPanel}
    </div>
  );
};

/**
 * Shared task detail dialog used by Task Management and Daily Tasks.
 */
const TaskViewDialog = ({
  task,
  onClose,
  onEdit,
  onProgressChange,
  onNavigateToMessage,
  footerActions,
  zIndexClass = 'z-50',
  usePortal = false,
  mappedTasks: mappedTasksProp,
  onAddSubtask,
  onEditSubtask,
  onDeleteSubtask,
  onCompleteSubtask,
  onCompleteTask,
  onUndoComplete,
  onDeleteTask,
  completing = false,
  savingSubtask = false,
  subtasksReadOnly = false,
  workTimerDisabled = false,
  onDelegated,
  onOpenWorkflow,
  mappedMessagesByTaskId = {},
}) => {
  const dispatch = useDispatch();
  const { promptCompletionTime, completionTimeDialog } = useCompletionTimeDialog();
  const { promptUrgentDeadline, urgentDeadlineDialog } = useUrgentDeadlineDialog();
  const [markingUrgent, setMarkingUrgent] = useState(false);
  const { user } = useSelector((state) => state.auth);
  const { users = [], loading: usersLoading } = useSelector((state) => state.users);
  const { channels = [], directMessages = [] } = useSelector((state) => state.channels);
  const chatMessages = useSelector((state) => state.chat.messages || {});

  const [mappedTasks, setMappedTasks] = useState([]);
  const [loadingMappedTasks, setLoadingMappedTasks] = useState(false);
  const [taskMessage, setTaskMessage] = useState(null);
  const [taskChannel, setTaskChannel] = useState(null);
  const [loadingTaskMessage, setLoadingTaskMessage] = useState(false);
  const [viewingImage, setViewingImage] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [reminderOpen, setReminderOpen] = useState(false);
  const [addingSubtask, setAddingSubtask] = useState(false);
  const [liveTask, setLiveTask] = useState(task);
  const [delegateOpen, setDelegateOpen] = useState(false);
  const [delegateQuery, setDelegateQuery] = useState('');
  const [delegating, setDelegating] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignQuery, setAssignQuery] = useState('');
  const [savingTask, setSavingTask] = useState(false);
  const [linkedWorkflows, setLinkedWorkflows] = useState([]);
  const [loadingWorkflows, setLoadingWorkflows] = useState(false);
  const [workflowSearchOpen, setWorkflowSearchOpen] = useState(false);
  const [mappingWorkflow, setMappingWorkflow] = useState(false);
  const [unmappingWorkflowId, setUnmappingWorkflowId] = useState(null);
  const [titleDraft, setTitleDraft] = useState(task?.title || '');
  const [descriptionDraft, setDescriptionDraft] = useState(task?.description || '');
  const [deletingTask, setDeletingTask] = useState(false);
  const [localClosing, setLocalClosing] = useState(false);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [resolvedCommentChannelId, setResolvedCommentChannelId] = useState(null);
  const [localMappedMessagesByTaskId, setLocalMappedMessagesByTaskId] = useState({});
  const [popupTab, setPopupTab] = useState('details');
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const [reactionPickerPosition, setReactionPickerPosition] = useState(null);
  const commentsEndRef = useRef(null);
  const commentsScrollRef = useRef(null);
  const descriptionQuillRef = useRef(null);
  const shouldStickToLatestComment = useRef(true);
  const reactionTriggerRef = useRef(null);
  const pendingReactionClicks = useRef(new Set());

  useEffect(() => {
    setLiveTask((prev) => {
      if (!task) return task;
      if (
        prev
        && String(prev.id) === String(task.id)
        && (prev.commentRootMessageId || prev.commentChannelId)
        && !task.commentRootMessageId
      ) {
        return {
          ...task,
          commentRootMessageId: prev.commentRootMessageId,
          commentChannelId: prev.commentChannelId,
        };
      }
      return task;
    });
    setTitleDraft(task?.title || '');
    setDescriptionDraft(normalizeDescriptionHtml(task?.description || ''));
    setDelegateOpen(false);
    setDelegateQuery('');
  }, [task]);

  useEffect(() => {
    if (!task?.id) return undefined;
    if (task.commentRootMessageId && task.commentChannelId) return undefined;
    if (task.messageId) return undefined;
    let cancelled = false;
    const token = localStorage.getItem('token');
    axios.get(`${API_URL}/tasks/${task.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((response) => {
      if (!cancelled && response.data?.id) setLiveTask(response.data);
    }).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [task?.id, task?.commentRootMessageId, task?.commentChannelId, task?.messageId]);

  useEffect(() => {
    setPopupTab('details');
  }, [task?.id]);

  useEffect(() => {
    if (!showReactionPicker) {
      setReactionPickerPosition(null);
      return undefined;
    }
    setReactionPickerPosition(computeReactionPickerPosition(reactionTriggerRef.current));
    return undefined;
  }, [showReactionPicker]);

  useEffect(() => {
    if (!showReactionPicker) return undefined;
    const onResize = () => {
      setReactionPickerPosition(computeReactionPickerPosition(reactionTriggerRef.current));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [showReactionPicker]);

  useEffect(() => {
    if (!showReactionPicker) return undefined;
    const onPointerDown = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('[data-reaction-picker]') || target.closest('[data-task-reaction-trigger]')) {
        return;
      }
      setShowReactionPicker(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        setShowReactionPicker(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [showReactionPicker]);

  useEffect(() => {
    if (!users?.length && !usersLoading) {
      dispatch(fetchUsers());
    }
  }, [dispatch, users?.length, usersLoading]);

  useEffect(() => {
    if (mappedTasksProp !== undefined) return;
    const fetchMappedTasks = async () => {
      if (!task?.id) {
        setMappedTasks([]);
        return;
      }
      setLoadingMappedTasks(true);
      setMappedTasks([]);
      try {
        const result = await dispatch(fetchTasksByMapTaskId(task.id)).unwrap();
        setMappedTasks(Array.isArray(result) ? result : []);
      } catch (error) {
        console.error('Failed to fetch mapped tasks:', error);
        setMappedTasks([]);
      } finally {
        setLoadingMappedTasks(false);
      }
    };

    fetchMappedTasks();
  }, [dispatch, task?.id, mappedTasksProp]);

  useEffect(() => {
    setAddingSubtask(false);
  }, [task?.id]);

  useEffect(() => {
    const fetchTaskMessage = async () => {
      const messageId = (liveTask || task)?.messageId;
      if (!messageId) {
        setTaskMessage(null);
        setTaskChannel(null);
        return;
      }

      setLoadingTaskMessage(true);
      setTaskMessage(null);
      setTaskChannel(null);

      try {
        let allChannels = [...channels, ...directMessages];
        if (allChannels.length === 0) {
          const [channelsResult, directMessagesResult] = await Promise.all([
            dispatch(fetchChannels()),
            dispatch(fetchDirectMessages()),
          ]);
          allChannels = [
            ...(channelsResult.payload || []),
            ...(directMessagesResult.payload || []),
          ];
        }

        const message = await resolveLocalMessage(messageId, chatMessages);
        let resolved = message || null;
        const token = localStorage.getItem('token');
        try {
          const response = await axios.get(`${API_URL}/messages/${messageId}`, {
            headers: { Authorization: token ? `Bearer ${token}` : '' },
          });
          if (response.data && typeof response.data === 'object' && !response.data.error) {
            resolved = response.data;
          }
        } catch (err) {
          console.warn('Failed to fetch linked message by id:', err);
        }
        const foundChannel = resolved?.channelId
          ? allChannels.find((c) => c.id === resolved.channelId) || null
          : null;
        if (resolved) {
          setTaskMessage(resolved);
          setTaskChannel(foundChannel);
        }
      } catch (error) {
        console.error('Failed to fetch task message:', error);
        setTaskMessage(null);
        setTaskChannel(null);
      } finally {
        setLoadingTaskMessage(false);
      }
    };

    fetchTaskMessage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, task?.messageId, liveTask?.messageId]);

  const linkedWorkflowIdsKey = taskWorkflowMessageIds(liveTask || task).join('|');

  useEffect(() => {
    let cancelled = false;
    const ids = linkedWorkflowIdsKey ? linkedWorkflowIdsKey.split('|').filter(Boolean) : [];
    if (ids.length === 0) {
      setLinkedWorkflows([]);
      setLoadingWorkflows(false);
      return undefined;
    }

    const fetchLinkedWorkflows = async () => {
      setLoadingWorkflows(true);
      const token = localStorage.getItem('token');
      try {
        const items = await Promise.all(ids.map(async (id) => {
          let resolved = await resolveLocalMessage(id, chatMessages);
          try {
            const response = await axios.get(`${API_URL}/messages/${id}`, {
              headers: { Authorization: token ? `Bearer ${token}` : '' },
            });
            if (response.data && typeof response.data === 'object' && !response.data.error) {
              resolved = response.data;
            }
          } catch (err) {
            console.warn('Failed to fetch mapped workflow by id:', err);
          }
          if (!resolved || !isWorkflowMessage(resolved)) return null;
          const item = messageToWorkflowItem(resolved);
          if (!item) return null;
          return {
            ...item,
            channelId: item.channelId || resolved.channelId || null,
          };
        }));
        if (!cancelled) setLinkedWorkflows(items.filter(Boolean));
      } catch (error) {
        console.error('Failed to fetch mapped workflows:', error);
        if (!cancelled) setLinkedWorkflows([]);
      } finally {
        if (!cancelled) setLoadingWorkflows(false);
      }
    };

    fetchLinkedWorkflows();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, linkedWorkflowIdsKey]);

  const commentRootMessageId = useMemo(() => {
    const current = liveTask || task;
    const primary = (current?.messageId && String(current.messageId).trim()) || '';
    if (primary) return primary;
    const commentRoot = (current?.commentRootMessageId && String(current.commentRootMessageId).trim()) || '';
    if (commentRoot) return commentRoot;
    const workflowIds = taskWorkflowMessageIds(current);
    if (workflowIds.length) return workflowIds[0];
    const mapped = (mappedTasksProp !== undefined
      ? (Array.isArray(mappedTasksProp) ? mappedTasksProp : [])
      : mappedTasks
    ).find((item) => item?.messageId && String(item.messageId).trim());
    return mapped?.messageId ? String(mapped.messageId).trim() : '';
  }, [liveTask, task, mappedTasks, mappedTasksProp]);

  const resolvedMappedTasks = useMemo(() => (
    mappedTasksProp !== undefined
      ? (Array.isArray(mappedTasksProp) ? mappedTasksProp : [])
      : mappedTasks
  ), [mappedTasksProp, mappedTasks]);

  const mappedChats = useMemo(
    () => resolvedMappedTasks.filter((item) => !isNamedSubtask(item)),
    [resolvedMappedTasks],
  );

  const effectiveMappedMessagesByTaskId = useMemo(
    () => ({ ...localMappedMessagesByTaskId, ...mappedMessagesByTaskId }),
    [localMappedMessagesByTaskId, mappedMessagesByTaskId],
  );

  const commentChannelId = useMemo(() => {
    if (resolvedCommentChannelId) return resolvedCommentChannelId;
    const current = liveTask || task;
    if (current?.commentChannelId) return current.commentChannelId;
    if (taskMessage?.channelId) return taskMessage.channelId;
    if (taskChannel?.id) return taskChannel.id;
    if (current?.channelId) return current.channelId;

    for (const mappedTask of mappedChats) {
      const mappedMessage = effectiveMappedMessagesByTaskId[mappedTask.id];
      if (mappedMessage?.channelId) return mappedMessage.channelId;
    }

    for (const workflow of linkedWorkflows) {
      if (workflow?.channelId) return workflow.channelId;
    }

    return null;
  }, [
    resolvedCommentChannelId,
    taskMessage?.channelId,
    taskChannel?.id,
    liveTask,
    task,
    mappedChats,
    effectiveMappedMessagesByTaskId,
    linkedWorkflows,
  ]);

  const canPostComment = Boolean(commentRootMessageId && commentChannelId);

  const commentChannelMemberIds = useMemo(() => {
    if (!commentChannelId) return (users || []).map((u) => u.id).filter(Boolean);
    const channel = [...channels, ...directMessages].find((ch) => String(ch.id) === String(commentChannelId));
    if (channel?.memberIds?.length) return [...channel.memberIds];
    return (users || []).map((u) => u.id).filter(Boolean);
  }, [commentChannelId, channels, directMessages, users]);

  useEffect(() => {
    setComments([]);
    setResolvedCommentChannelId(null);
    shouldStickToLatestComment.current = true;
  }, [commentRootMessageId]);

  useEffect(() => {
    if (!commentRootMessageId) return undefined;
    let cancelled = false;
    setLoadingComments(true);
    (async () => {
      try {
        const token = localStorage.getItem('token');
        let channelHint = commentChannelId;

        try {
          const rootResponse = await axios.get(`${API_URL}/messages/${commentRootMessageId}`, {
            headers: { Authorization: token ? `Bearer ${token}` : '' },
          });
          if (!cancelled && rootResponse.data?.channelId) {
            channelHint = rootResponse.data.channelId;
            setResolvedCommentChannelId(rootResponse.data.channelId);
          }
        } catch {
          // keep existing channel hint
        }

        // Pull nearby channel messages so local Redux/thread sync sees the same replies.
        if (channelHint) {
          try {
            const around = await axios.get(
              `${API_URL}/messages/channel/${channelHint}/around/${commentRootMessageId}`,
              {
                params: { before: 40, after: 40 },
                headers: { Authorization: token ? `Bearer ${token}` : '' },
              },
            );
            const aroundMsgs = around.data?.messages || [];
            if (!cancelled && aroundMsgs.length) {
              setComments((prev) => mergeComments(
                prev,
                collectThreadReplies(aroundMsgs, commentRootMessageId),
              ));
            }
          } catch {
            // optional enrichment
          }
        }

        const response = await axios.get(`${API_URL}/messages/${commentRootMessageId}/replies`, {
          headers: { Authorization: token ? `Bearer ${token}` : '' },
        });
        if (!cancelled && Array.isArray(response.data)) {
          setComments((prev) => mergeComments(prev, response.data));
        }
      } catch {
        // keep local replies
      } finally {
        if (!cancelled) setLoadingComments(false);
      }
    })();
    return () => { cancelled = true; };
  }, [commentRootMessageId, commentChannelId]);

  useEffect(() => {
    if (!commentRootMessageId || !commentChannelId) return;
    const channelMsgs = chatMessages[commentChannelId] || [];
    const localReplies = collectThreadReplies(channelMsgs, commentRootMessageId);
    if (localReplies.length) {
      setComments((prev) => mergeComments(prev, localReplies));
    }
  }, [chatMessages, commentChannelId, commentRootMessageId]);

  useEffect(() => {
    setLocalMappedMessagesByTaskId({});
  }, [task?.id]);

  useEffect(() => {
    let cancelled = false;
    const pending = mappedChats.filter(
      (mappedTask) => mappedTask.messageId
        && !effectiveMappedMessagesByTaskId[mappedTask.id],
    );
    if (pending.length === 0) return undefined;

    (async () => {
      const token = localStorage.getItem('token');
      const fetched = await Promise.all(pending.map(async (mappedTask) => {
        const messageId = String(mappedTask.messageId).trim();
        let resolved = await resolveLocalMessage(messageId, chatMessages);
        try {
          const response = await axios.get(`${API_URL}/messages/${messageId}`, {
            headers: { Authorization: token ? `Bearer ${token}` : '' },
          });
          if (response.data && typeof response.data === 'object' && !response.data.error) {
            resolved = response.data;
          }
        } catch {
          // keep local message if available
        }
        return resolved ? { taskId: mappedTask.id, message: resolved } : null;
      }));

      if (cancelled) return;
      setLocalMappedMessagesByTaskId((prev) => {
        const next = { ...prev };
        fetched.filter(Boolean).forEach(({ taskId, message }) => {
          next[taskId] = message;
        });
        return next;
      });
    })();

    return () => { cancelled = true; };
  }, [mappedChats, effectiveMappedMessagesByTaskId, chatMessages]);

  const chatTimeline = useMemo(() => {
    const items = [];
    const seenIds = new Set();
    const currentTask = liveTask || task;

    const addMessage = (message, meta = {}) => {
      if (!message) return;
      const id = commentKey(message);
      if (!id || seenIds.has(id)) return;
      seenIds.add(id);
      items.push({ type: 'message', id, message, ...meta });
    };

    const addTaskFallback = (mappedTask, meta = {}) => {
      items.push({ type: 'task', id: `task-${mappedTask.id}`, task: mappedTask, ...meta });
    };

    const primaryMessageId = (currentTask?.messageId && String(currentTask.messageId).trim()) || '';

    if (taskMessage) {
      addMessage(taskMessage, { navigateTask: currentTask });
    } else if (primaryMessageId && hasTaskDescription(currentTask)) {
      addTaskFallback(currentTask, { navigateTask: currentTask });
    }

    [...mappedChats]
      .sort((a, b) => commentTime({ createdAt: a.createdAt }) - commentTime({ createdAt: b.createdAt }))
      .forEach((mappedTask) => {
        if (mappedTask.id === currentTask?.id && primaryMessageId) return;
        const mappedMessage = effectiveMappedMessagesByTaskId[mappedTask.id];
        if (mappedMessage) {
          addMessage(mappedMessage, { navigateTask: mappedTask });
        } else if (hasTaskDescription(mappedTask)) {
          addTaskFallback(mappedTask, { navigateTask: mappedTask });
        }
      });

    comments.forEach((comment) => addMessage(comment, { isComment: true }));

    return items.sort((a, b) => {
      const timeA = a.type === 'message'
        ? commentTime(a.message)
        : commentTime({ createdAt: a.task?.createdAt });
      const timeB = b.type === 'message'
        ? commentTime(b.message)
        : commentTime({ createdAt: b.task?.createdAt });
      return timeA - timeB;
    });
  }, [
    taskMessage,
    liveTask,
    task,
    mappedChats,
    effectiveMappedMessagesByTaskId,
    comments,
  ]);

  const handleViewImage = useCallback((images, index = 0, context = {}) => {
    if (!images?.length) return;
    const fallbackChannelId = context.channelId || commentChannelId || null;
    const fallbackMessageId = context.messageId || null;
    const fallbackReplyTo = context.replyToMessageId || null;
    setViewingImage({
      images: images.map((image) => ({
        ...image,
        messageId: image.messageId || fallbackMessageId || null,
        channelId: image.channelId || fallbackChannelId || null,
        replyToMessageId: image.replyToMessageId || fallbackReplyTo || null,
      })),
      index: Number.isFinite(index) && index >= 0 ? index : 0,
    });
  }, [commentChannelId]);

  const handleAttachmentMessageUpdated = useCallback((message) => {
    if (!message?.id) return;
    setComments((prev) => mergeComments(prev, [message]));
  }, []);

  useEffect(() => {
    if (!shouldStickToLatestComment.current) return;
    const container = commentsScrollRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
      return;
    }
    commentsEndRef.current?.scrollIntoView({ block: 'end' });
  }, [chatTimeline.length]);

  const handleOpenWorkflow = useCallback((item) => {
    if (!item?.messageId) return;
    const payload = { ...item, sourceTask: liveTask || task };
    if (onOpenWorkflow) {
      onClose?.();
      onOpenWorkflow(payload);
      return;
    }
    onClose?.();
    window.dispatchEvent(new CustomEvent('workloop:open-workflow', { detail: payload }));
  }, [onOpenWorkflow, onClose, liveTask, task]);

  if (!task) return null;

  const displayTask = liveTask || task;

  const getUserDisplayName = (userId) => {
    if (!userId) return null;
    const match = (users || []).find((u) => sameUserId(u.id, userId));
    if (match) {
      if (match.firstName || match.lastName) {
        return `${match.firstName || ''} ${match.lastName || ''}`.trim();
      }
      return match.username || match.email || null;
    }
    return null;
  };

  const getDelegateNameList = (delegateId) => {
    if (!delegateId) return [];
    const ids = Array.isArray(delegateId) ? delegateId : [delegateId];
    if (ids.length === 0) return [];
    return ids.map((id) => {
      if (sameUserId(id, user?.id)) return 'You';
      return getUserDisplayName(id) || id;
    });
  };

  const getDelegationInfo = () => {
    if (!displayTask.delegateId || !user) return null;

    const ids = Array.isArray(displayTask.delegateId) ? displayTask.delegateId : [displayTask.delegateId];
    if (ids.length === 0) return null;

    const isCreatedByMe = sameUserId(displayTask.createdByUserId, user.id);
    const isDelegatedToMe = ids.some((id) => sameUserId(id, user.id));
    const delegatedToOthers = ids.filter((id) => !sameUserId(id, user.id));
    const isDelegatedByMe = isCreatedByMe && delegatedToOthers.length > 0;

    if (!isDelegatedByMe && !(isDelegatedToMe && !isCreatedByMe)) {
      return null;
    }

    if (isDelegatedByMe) {
      return {
        label: 'Delegated to',
        names: getDelegateNameList(delegatedToOthers),
      };
    }
    if (isDelegatedToMe && !isCreatedByMe) {
      const delegatorName = getUserDisplayName(displayTask.createdByUserId);
      return {
        label: 'Delegated by',
        names: delegatorName ? [delegatorName] : getDelegateNameList(displayTask.createdByUserId),
        suffix: delegatedToOthers.length > 0 ? '(to you and others)' : undefined,
      };
    }
    return null;
  };

  const renderTaskAttachments = (attachments) => {
    if (!attachments || attachments.length === 0) return null;

    return (
      <div className="mt-3" onClick={(event) => event.stopPropagation()}>
        <AttachmentGroupBar
          attachments={attachments}
          groupName={taskMessage?.attachmentGroupName || ''}
          expanded
          onViewImage={(images, index) => handleViewImage(images, index, {
            messageId: taskMessage?.id || displayTask?.messageId || null,
            channelId: taskMessage?.channelId || commentChannelId || null,
          })}
          onDownload={handleCommentDownload}
          mode="display"
        />
      </div>
    );
  };

  const handleGoToMessage = (targetTask) => {
    const messageId = String(targetTask?.messageId || displayTask?.messageId || '').trim();
    if (!messageId) return;

    const channelId =
      targetTask?.id === task?.id || targetTask?.id === displayTask?.id
        ? (
          taskChannel?.id
          || targetTask?.channelId
          || taskMessage?.channelId
          || commentChannelId
          || displayTask?.channelId
          || null
        )
        : (targetTask?.channelId || commentChannelId || null);

    const payload = {
      ...(targetTask || displayTask || task),
      messageId,
      channelId: channelId || targetTask?.channelId || null,
      replyToMessageId: targetTask?.replyToMessageId || taskMessage?.replyToMessageId || null,
    };

    if (onNavigateToMessage) {
      onNavigateToMessage(payload);
      return;
    }

    if (payload.channelId) {
      onClose?.();
      window.dispatchEvent(new CustomEvent('workloop:go-to-message', { detail: payload }));
    }
  };

  const handleCommentDownload = async (attachment) => {
    try {
      await downloadAttachment(attachment);
    } catch {
      openAttachmentFallback(attachment);
    }
  };

  const handleAddComment = async (
    content,
    attachments = [],
    replyToMessageId = null,
    linkPreview = null,
    mentionedUserIds = [],
    attachmentGroupName = null,
    targetResourceGroupId = null,
  ) => {
    if (!commentChannelId || !commentRootMessageId) return;
    const hasText = Boolean(content && String(content).replace(/<[^>]*>/g, '').trim());
    const hasAttachments = Array.isArray(attachments) && attachments.length > 0;
    if (!hasText && !hasAttachments) return;

    try {
      const sent = await dispatch(sendMessage({
        content: content || '',
        channelId: commentChannelId,
        replyToMessageId: replyToMessageId || commentRootMessageId,
        senderId: user?.id,
        mentionedUserIds: mentionedUserIds?.length > 0 ? mentionedUserIds : undefined,
        attachments: hasAttachments ? attachments : undefined,
        attachmentGroupName: attachmentGroupName || undefined,
        targetResourceGroupId: targetResourceGroupId || undefined,
        linkPreview: linkPreview || undefined,
      })).unwrap();
      if (sent) {
        shouldStickToLatestComment.current = true;
        const currentUserName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim()
          || user?.username
          || 'You';
        setComments((prev) => mergeComments(prev, [{
          ...sent,
          senderId: sent.senderId || user?.id,
          senderName: sent.senderName || currentUserName,
          senderProfilePicture: sent.senderProfilePicture || user?.profilePicture,
          createdAt: sent.createdAt || sent.timestamp || new Date().toISOString(),
        }]));
      }
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: apiErrorMessage(err, 'Failed to add comment'),
      }));
      throw err;
    }
  };

  const handleEditComment = async (comment, content) => {
    const id = commentKey(comment);
    if (!id) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/messages/${id}`,
        { content },
        { headers: { Authorization: token ? `Bearer ${token}` : '' } },
      );
      const updated = {
        ...comment,
        ...response.data,
        isEdited: true,
      };
      setComments((prev) => mergeComments(prev, [updated]));
      if (updated.channelId) {
        dispatch(addMessage({
          channelId: updated.channelId,
          message: updated,
          skipUnreadCountIncrement: true,
        }));
      }
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: apiErrorMessage(err?.response?.data || err, 'Failed to edit comment'),
      }));
      throw err;
    }
  };

  const handleDeleteComment = async (comment) => {
    const id = commentKey(comment);
    if (!id) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/messages/${id}`, {
        headers: { Authorization: token ? `Bearer ${token}` : '' },
      });
      setComments((prev) => removeCommentById(prev, id));
      if (comment.channelId) {
        dispatch(removeMessage({ channelId: comment.channelId, messageId: id }));
      }
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: apiErrorMessage(err?.response?.data || err, 'Failed to delete comment'),
      }));
      throw err;
    }
  };

  const delegateIds = taskDelegateIds(displayTask);
  const canDelegate = Boolean(user?.id)
    && displayTask.status !== TaskStatus.COMPLETED
    && displayTask.status !== TaskStatus.CANCELLED;
  const canMapWorkflow = Boolean(user?.id)
    && displayTask.status !== TaskStatus.CANCELLED;
  const delegateUsers = (() => {
    const people = [...(users || [])];
    if (user?.id && !people.some((person) => sameUserId(person.id, user.id))) {
      people.unshift(user);
    }
    return people
      .filter((person) => person && person.isActive !== false)
      .map((person) => {
        const isSelf = sameUserId(person.id, user?.id);
        const name = [person.firstName, person.lastName].filter(Boolean).join(' ').trim()
          || person.username
          || person.email
          || 'Unknown user';
        return {
          id: person.id,
          isSelf,
          label: isSelf ? `You (${name})` : name,
        };
      })
      .sort((a, b) => {
        if (a.isSelf !== b.isSelf) return a.isSelf ? -1 : 1;
        return a.label.localeCompare(b.label);
      });
  })();
  const selectedDelegateLabel = getDelegateNameList(delegateIds).join(', ');
  const chatLinkedTask = Boolean(String(displayTask.messageId || '').trim());
  const linkedMessageIsWorkflow = isWorkflowMessage(taskMessage);
  const taskAssigneeId = delegateIds[0] || displayTask.createdByUserId || null;
  const assigneeId = taskAssigneeId;
  const assigneeName = selectedDelegateLabel
    || (sameUserId(assigneeId, user?.id)
      ? `You (${getUserDisplayName(assigneeId) || 'me'})`
      : (getUserDisplayName(assigneeId) || ''));
  const chatAssignableUsers = (() => {
    const memberIds = taskChannel?.memberIds;
    const source = Array.isArray(memberIds) && memberIds.length > 0
      ? memberIds.map((id) => users.find((person) => sameUserId(person?.id, id))).filter(Boolean)
      : [...(users || [])];
    const people = [...source];
    if (user?.id && !people.some((person) => sameUserId(person.id, user.id))) {
      people.unshift(user);
    }
    return people
      .filter((person) => person && person.isActive !== false)
      .map((person) => {
        const isSelf = sameUserId(person.id, user?.id);
        const name = [person.firstName, person.lastName].filter(Boolean).join(' ').trim()
          || person.username
          || person.email
          || 'Unknown user';
        return {
          id: person.id,
          isSelf,
          label: isSelf ? `You (${name})` : name,
        };
      })
      .sort((a, b) => {
        if (a.isSelf !== b.isSelf) return a.isSelf ? -1 : 1;
        return a.label.localeCompare(b.label);
      });
  })();

  const handleAssignChatTask = async (userId) => {
    if (!canDelegate || !displayTask.id || delegating) return;
    const nextIds = !userId || sameUserId(userId, displayTask.createdByUserId)
      ? []
      : [userId];
    setDelegating(true);
    try {
      const updated = await dispatch(updateTask({
        taskId: displayTask.id,
        taskData: { delegateId: nextIds },
      })).unwrap();
      setLiveTask(updated || { ...displayTask, delegateId: nextIds });
      onDelegated?.(updated || { ...displayTask, delegateId: nextIds });

      const messageId = String(displayTask.messageId || taskMessage?.id || '').trim();
      if (messageId && !linkedMessageIsWorkflow) {
        const updatedMessage = await dispatch(assignWorkflowMessage({ messageId, userId })).unwrap();
        if (updatedMessage) setTaskMessage(updatedMessage);
      }

      setAssignOpen(false);
      setAssignQuery('');
      dispatch(fetchDirectMessages());
      dispatch(addNotification({
        type: 'success',
        message: 'Task assigned',
      }));
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: typeof err === 'string' ? err : 'Failed to assign task',
      }));
    } finally {
      setDelegating(false);
    }
  };

  const handleToggleDelegate = async (userId) => {
    if (!canDelegate || !displayTask.id || delegating) return;
    const nextIds = delegateIds.some((id) => sameUserId(id, userId))
      ? delegateIds.filter((id) => !sameUserId(id, userId))
      : [...delegateIds, userId];
    setDelegating(true);
    try {
      const updated = await dispatch(updateTask({
        taskId: displayTask.id,
        taskData: { delegateId: nextIds },
      })).unwrap();
      setLiveTask(updated || { ...displayTask, delegateId: nextIds });
      onDelegated?.(updated || { ...displayTask, delegateId: nextIds });
      dispatch(fetchDirectMessages());
      dispatch(addNotification({
        type: 'success',
        message: nextIds.length > 0 ? 'Task delegated' : 'Delegation removed',
      }));
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: typeof err === 'string' ? err : 'Failed to update delegates',
      }));
    } finally {
      setDelegating(false);
    }
  };

  const handlePostCompletionChange = async ({ enabled, actions }) => {
    if (!displayTask?.id || !canEditTask) return;
    const previous = {
      postCompletionEnabled: Boolean(displayTask.postCompletionEnabled),
      postCompletionActions: displayTask.postCompletionActions || [],
    };
    setLiveTask((prev) => ({
      ...(prev || displayTask),
      postCompletionEnabled: enabled,
      postCompletionActions: actions,
    }));
    try {
      const updated = await dispatch(updateTask({
        taskId: displayTask.id,
        taskData: {
          postCompletionEnabled: enabled,
          postCompletionActions: actions,
        },
      })).unwrap();
      setLiveTask(updated || { ...displayTask, postCompletionEnabled: enabled, postCompletionActions: actions });
    } catch (err) {
      setLiveTask((prev) => ({ ...(prev || displayTask), ...previous }));
      dispatch(addNotification({
        type: 'error',
        message: typeof err === 'string' ? err : 'Failed to save post completion',
      }));
    }
  };

  const handleSaveTask = async () => {
    if (!displayTask?.id || savingTask) return;
    setSavingTask(true);
    try {
      const editor = descriptionQuillRef.current?.getEditor();
      const descriptionHtml = editor ? editor.root.innerHTML : descriptionDraft;
      const updated = await dispatch(updateTask({
        taskId: displayTask.id,
        taskData: {
          title: titleDraft.trim() || displayTask.title || '',
          description: descriptionHtml,
          messageId: displayTask.messageId || '',
          mapTaskId: displayTask.mapTaskId || '',
          workflowMessageIds: taskWorkflowMessageIds(displayTask),
          delegateId: delegateIds,
          status: displayTask.status,
          completionPercentage: displayTask.completionPercentage,
          estimateTimeInMinutes: displayTask.estimateTimeInMinutes,
          spaceId: displayTask.spaceId || '',
          spaceName: displayTask.spaceName || '',
        },
      })).unwrap();
      setLiveTask(updated || { ...displayTask, description: descriptionHtml });
      onDelegated?.(updated || displayTask);
      dispatch(addNotification({
        type: 'success',
        message: 'Task saved',
      }));
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: typeof err === 'string' ? err : 'Failed to save task',
      }));
    } finally {
      setSavingTask(false);
    }
  };

  const handleSaveTaskSpace = async ({ spaceId, spaceName }) => {
    if (!displayTask?.id) return;
    const previous = {
      spaceId: displayTask.spaceId || '',
      spaceName: displayTask.spaceName || '',
    };
    setLiveTask((prev) => ({
      ...(prev || displayTask),
      spaceId,
      spaceName,
    }));
    try {
      const updated = await dispatch(updateTask({
        taskId: displayTask.id,
        taskData: { spaceId, spaceName },
      })).unwrap();
      setLiveTask(updated || { ...displayTask, spaceId, spaceName });
      window.dispatchEvent(new CustomEvent('workloop:tasks-flushed'));
    } catch (err) {
      setLiveTask((prev) => ({ ...(prev || displayTask), ...previous }));
      dispatch(addNotification({
        type: 'error',
        message: typeof err === 'string' ? err : 'Failed to update space',
      }));
    }
  };

  const handleMapSelectedWorkflow = async (item) => {
    const messageId = item?.messageId;
    if (!displayTask?.id || !messageId || mappingWorkflow) return;
    setMappingWorkflow(true);
    try {
      const updated = await dispatch(mapTaskToWorkflow({
        taskId: displayTask.id,
        messageId,
      })).unwrap();
      setLiveTask(updated || displayTask);
      onDelegated?.(updated || displayTask);
      setWorkflowSearchOpen(false);
      dispatch(addNotification({
        type: 'success',
        message: 'Workflow mapped',
      }));
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: typeof err === 'string' ? err : 'Failed to map workflow',
      }));
    } finally {
      setMappingWorkflow(false);
    }
  };

  const handleUnmapWorkflow = async (event, messageId) => {
    event?.stopPropagation?.();
    if (!displayTask?.id || !messageId || unmappingWorkflowId) return;
    setUnmappingWorkflowId(messageId);
    try {
      const updated = await dispatch(unmapTaskFromWorkflow({
        taskId: displayTask.id,
        messageId,
      })).unwrap();
      setLiveTask(updated || displayTask);
      onDelegated?.(updated || displayTask);
      dispatch(addNotification({
        type: 'success',
        message: 'Workflow unmapped',
      }));
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: typeof err === 'string' ? err : 'Failed to unmap workflow',
      }));
    } finally {
      setUnmappingWorkflowId(null);
    }
  };

  const refreshMappedTasks = async () => {
    if (!displayTask?.id || mappedTasksProp !== undefined) return;
    try {
      const result = await dispatch(fetchTasksByMapTaskId(displayTask.id)).unwrap();
      setMappedTasks(Array.isArray(result) ? result : []);
    } catch {
      // keep current mapped tasks
    }
  };

  const handleProgressChange = async (pct) => {
    if (onProgressChange) {
      await onProgressChange(pct);
      return;
    }
    if (!displayTask?.id) return;
    try {
      let completionTimeInMinutes;
      if (pct >= 100 && displayTask.status !== TaskStatus.COMPLETED) {
        const currentMapped = mappedTasksProp !== undefined
          ? (Array.isArray(mappedTasksProp) ? mappedTasksProp : [])
          : mappedTasks;
        const subtaskError = unfinishedSubtasksError(currentMapped);
        if (subtaskError) {
          dispatch(addNotification({ type: 'error', message: subtaskError }));
          return;
        }
        const minutes = await promptCompletionTime({
          kind: 'task',
          targetType: 'TASK',
          targetId: displayTask.id,
        });
        if (minutes == null) return;
        completionTimeInMinutes = minutes;
      }
      const updated = await dispatch(updateTaskProgress({
        taskId: displayTask.id,
        completionPercentage: pct,
        task: displayTask,
        completionTimeInMinutes,
      })).unwrap();
      setLiveTask(updated || { ...displayTask, completionPercentage: pct });
      onDelegated?.(updated || displayTask);
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: apiErrorMessage(err, 'Failed to update progress'),
      }));
    }
  };

  const handleAddSubtaskInternal = async (payload) => {
    if (onAddSubtask) {
      await onAddSubtask(payload);
      return;
    }
    if (!displayTask?.id) return;
    try {
      const created = await dispatch(createTask({
        title: payload.title,
        estimateTimeInMinutes: payload.estimateTimeInMinutes,
        mapTaskId: displayTask.id,
      })).unwrap();
      if (created) {
        setMappedTasks((prev) => [...prev, created]);
      } else {
        await refreshMappedTasks();
      }
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: typeof err === 'string' ? err : 'Failed to add subtask',
      }));
      throw err;
    }
  };

  const handleEditSubtaskInternal = async (subtask, payload) => {
    if (onEditSubtask) {
      await onEditSubtask(subtask, payload);
      return;
    }
    if (!subtask?.id) return;
    const updated = await dispatch(updateTask({
      taskId: subtask.id,
      taskData: {
        title: payload.title,
        estimateTimeInMinutes: payload.estimateTimeInMinutes,
      },
    })).unwrap();
    setMappedTasks((prev) => prev.map((item) => (
      item.id === subtask.id ? { ...item, ...(updated || {}), ...payload } : item
    )));
  };

  const handleDeleteSubtaskInternal = async (subtask) => {
    if (onDeleteSubtask) {
      await onDeleteSubtask(subtask);
      return;
    }
    if (!subtask?.id) return;
    await dispatch(deleteTask(subtask.id)).unwrap();
    setMappedTasks((prev) => prev.filter((item) => item.id !== subtask.id));
  };

  const handleCompleteSubtaskInternal = async (subtask) => {
    if (onCompleteSubtask) {
      await onCompleteSubtask(subtask);
      return;
    }
    if (!subtask?.id) return;
    const minutes = await promptCompletionTime({
      kind: 'subtask',
      targetType: 'TASK',
      targetId: subtask.id,
    });
    if (minutes == null) return;
    const updated = await dispatch(updateTask({
      taskId: subtask.id,
      taskData: { completionTimeInMinutes: minutes },
    })).unwrap();
    setMappedTasks((prev) => prev.map((item) => (
      item.id === subtask.id
        ? { ...item, ...(updated || {}), completionTimeInMinutes: minutes }
        : item
    )));
  };

  const handleCompleteTaskInternal = async () => {
    if (onCompleteTask) {
      await onCompleteTask();
      return;
    }
    if (!displayTask?.id) return;
    const currentMapped = mappedTasksProp !== undefined
      ? (Array.isArray(mappedTasksProp) ? mappedTasksProp : [])
      : mappedTasks;
    const subtaskError = unfinishedSubtasksError(currentMapped);
    if (subtaskError) {
      dispatch(addNotification({ type: 'error', message: subtaskError }));
      return;
    }
    const minutes = await promptCompletionTime({
      kind: 'task',
      targetType: 'TASK',
      targetId: displayTask.id,
    });
    if (minutes == null) return;
    setLocalClosing(true);
    try {
      const updated = await dispatch(updateTask({
        taskId: displayTask.id,
        taskData: {
          status: TaskStatus.COMPLETED,
          completionPercentage: 100,
          ...(Number(minutes) > 0 ? { completionTimeInMinutes: minutes } : {}),
        },
      })).unwrap();
      setLiveTask(updated || { ...displayTask, status: TaskStatus.COMPLETED, completionPercentage: 100 });
      onDelegated?.(updated || displayTask);
      dispatch(addNotification({ type: 'success', message: 'Task completed' }));
      await new Promise((resolve) => window.setTimeout(resolve, 900));
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: apiErrorMessage(err, 'Failed to complete task'),
      }));
    } finally {
      setLocalClosing(false);
    }
  };

  const persistCompletionMinutes = async (task, minutes) => {
    if (!task?.id || !(Number(minutes) > 0)) return null;
    const updated = await dispatch(updateTask({
      taskId: task.id,
      taskData: { completionTimeInMinutes: minutes },
    })).unwrap();
    return { ...task, ...(updated || {}), completionTimeInMinutes: minutes };
  };

  const handleEditCompletionTime = async () => {
    if (!displayTask?.id) return;
    const minutes = await promptCompletionTime({
      kind: 'task',
      initialMinutes: Number(displayTask.completionTimeInMinutes) > 0
        ? Number(displayTask.completionTimeInMinutes)
        : undefined,
      confirmLabel: 'Save time',
    });
    if (minutes == null) return;
    try {
      const next = await persistCompletionMinutes(displayTask, minutes);
      if (next) {
        setLiveTask(next);
        onDelegated?.(next);
        dispatch(addNotification({ type: 'success', message: 'Time spent updated' }));
      }
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: apiErrorMessage(err, 'Failed to update time spent'),
      }));
    }
  };

  const handleEditSubtaskTime = async (subtask) => {
    if (!subtask?.id) return;
    const minutes = await promptCompletionTime({
      kind: 'subtask',
      initialMinutes: Number(subtask.completionTimeInMinutes) > 0
        ? Number(subtask.completionTimeInMinutes)
        : undefined,
      confirmLabel: 'Save time',
    });
    if (minutes == null) return;
    try {
      const next = await persistCompletionMinutes(subtask, minutes);
      if (next) {
        setMappedTasks((prev) => prev.map((item) => (item.id === subtask.id ? next : item)));
        dispatch(addNotification({ type: 'success', message: 'Time spent updated' }));
      }
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: apiErrorMessage(err, 'Failed to update time spent'),
      }));
    }
  };

  const handleMarkTaskUrgent = async () => {
    if (!displayTask?.id || markingUrgent) return;
    if (displayTask.status === TaskStatus.COMPLETED || displayTask.status === TaskStatus.CANCELLED) return;
    const deadline = await promptUrgentDeadline();
    if (!deadline) return;
    setMarkingUrgent(true);
    try {
      const updated = await dispatch(updateTask({
        taskId: displayTask.id,
        taskData: { urgentDeadlineAt: deadline },
      })).unwrap();
      const next = { ...(updated || displayTask), urgentDeadlineAt: updated?.urgentDeadlineAt || deadline };
      setLiveTask(next);
      onDelegated?.(next);
      dispatch(addNotification({ type: 'success', message: 'Task marked urgent' }));
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: apiErrorMessage(err, 'Failed to mark task urgent'),
      }));
    } finally {
      setMarkingUrgent(false);
    }
  };

  const handleClearTaskUrgent = async () => {
    if (!displayTask?.id || markingUrgent) return;
    if (displayTask.status === TaskStatus.COMPLETED || displayTask.status === TaskStatus.CANCELLED) return;
    setMarkingUrgent(true);
    try {
      const updated = await dispatch(updateTask({
        taskId: displayTask.id,
        taskData: { clearUrgentDeadline: true },
      })).unwrap();
      const next = { ...(updated || displayTask), urgentDeadlineAt: null };
      setLiveTask(next);
      onDelegated?.(next);
      dispatch(addNotification({ type: 'success', message: 'Urgent cleared' }));
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: apiErrorMessage(err, 'Failed to clear urgent'),
      }));
    } finally {
      setMarkingUrgent(false);
    }
  };

  const handleDeleteTaskInternal = async () => {
    if (onDeleteTask) {
      await onDeleteTask();
      return;
    }
    if (!displayTask?.id || deletingTask) return;
    if (!window.confirm('Delete this task?')) return;
    setDeletingTask(true);
    try {
      await dispatch(deleteTask(displayTask.id)).unwrap();
      dispatch(addNotification({ type: 'success', message: 'Task deleted' }));
      onClose();
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: typeof err === 'string' ? err : 'Failed to delete task',
      }));
    } finally {
      setDeletingTask(false);
    }
  };

  const delegationInfo = getDelegationInfo();
  const namedSubtasks = resolvedMappedTasks.filter(isNamedSubtask);
  const unfinishedSubtasks = namedSubtasks.filter((item) => !isSubtaskFinished(item)).length;
  const canCompleteParent = !loadingMappedTasks && unfinishedSubtasks === 0;
  const totalMinutes = namedSubtasks.length > 0
    ? namedSubtasks.reduce((sum, item) => {
      const actual = Number(item.completionTimeInMinutes);
      if (Number.isFinite(actual) && actual > 0) return sum + actual;
      return sum + (Number(item.estimateTimeInMinutes) || 0);
    }, 0)
    : (Number(displayTask.completionTimeInMinutes) > 0
      ? Number(displayTask.completionTimeInMinutes)
      : Number(displayTask.estimateTimeInMinutes) || 0);
  const progressPct = normalizeTaskProgress(displayTask.completionPercentage, displayTask.status);
  const done = displayTask.status === TaskStatus.COMPLETED;
  const canEditTask = !done && displayTask.status !== TaskStatus.CANCELLED;
  const canEditCompletionTime = displayTask.status !== TaskStatus.CANCELLED && !workTimerDisabled;
  const spaceLocked = Boolean(
    explicitWorkflowMessageIds(displayTask).length > 0
    || (linkedWorkflows || []).length > 0
    || isWorkflowMessage(taskMessage)
  );
  const displaySpaceName = displayTask.spaceName
    || taskMessage?.workflowSpaceName
    || linkedWorkflows[0]?.workflowSpaceName
    || '';
  const displaySpaceId = displayTask.spaceId
    || taskMessage?.workflowSpaceId
    || linkedWorkflows[0]?.workflowSpaceId
    || '';
  const isUrgentTask = Boolean(parseUrgentDeadline(displayTask.urgentDeadlineAt)) && canEditTask;
  const canAddSubtask = canEditTask && !subtasksReadOnly;
  const canDeleteTask = sameUserId(displayTask.createdByUserId, user?.id) && !done;

  const linkedChatMessageId = String(displayTask?.messageId || '').trim();
  const linkedChatChannelId = commentChannelId
    || taskChannel?.id
    || taskMessage?.channelId
    || displayTask?.channelId
    || null;
  const canGoToLinkedChat = Boolean(
    linkedChatMessageId
    && (onNavigateToMessage || linkedChatChannelId)
  );

  const currentUserId = user?.id;
  const resolvedTaskReactions = Array.isArray(taskMessage?.reactions) ? taskMessage.reactions : [];

  const toggleReactionPicker = (event) => {
    const messageId = linkedChatMessageId || String(taskMessage?.id || '').trim();
    if (!messageId) {
      dispatch(addNotification({
        type: 'error',
        message: 'Link this task to a chat message to add icons',
      }));
      return;
    }
    const el = event.currentTarget;
    if (showReactionPicker && reactionTriggerRef.current === el) {
      setShowReactionPicker(false);
      return;
    }
    reactionTriggerRef.current = el;
    setReactionPickerPosition(computeReactionPickerPosition(el));
    setShowReactionPicker(true);
  };

  const handleReactionClick = async (reactionId) => {
    const messageId = linkedChatMessageId || String(taskMessage?.id || '').trim();
    if (!messageId || !reactionId || !currentUserId) return;
    const mappedReactionId = resolveUrgentWorkflowReactionId(reactionId);
    const reactionKey = isWorkflowReaction(mappedReactionId) ? mappedReactionId.trim() : mappedReactionId;
    if (!reactionKey) return;
    if (reactionKey.startsWith('wf:work-item-type:')) {
      setShowReactionPicker(false);
      return;
    }

    let urgentDeadlineAt;
    if (reactionKey === WORKFLOW_URGENT_ID && !messageHasUrgentWorkflow(resolvedTaskReactions)) {
      const deadline = await promptUrgentDeadline();
      if (!deadline) return;
      urgentDeadlineAt = deadline;
    }

    const pendingKey = `${messageId}:${reactionKey}`;
    if (pendingReactionClicks.current.has(pendingKey)) return;
    pendingReactionClicks.current.add(pendingKey);
    setShowReactionPicker(false);
    const previous = taskMessage;
    setTaskMessage((prev) => {
      const base = prev || { id: messageId, reactions: [] };
      return {
        ...base,
        reactions: applyOptimisticReaction(base.reactions || [], reactionKey, currentUserId),
        ...(urgentDeadlineAt ? { urgentDeadlineAt } : {}),
      };
    });
    try {
      const updated = await dispatch(addReaction({
        messageId,
        emoji: reactionKey,
        channelId: linkedChatChannelId,
        userId: currentUserId,
        urgentDeadlineAt,
      })).unwrap();
      if (updated && typeof updated === 'object') {
        setTaskMessage((prev) => ({ ...(prev || {}), ...updated }));
      }
    } catch (err) {
      setTaskMessage(previous);
      dispatch(addNotification({
        type: 'error',
        message: apiErrorMessage(err, 'Failed to add icon'),
      }));
    } finally {
      pendingReactionClicks.current.delete(pendingKey);
    }
  };

  const closingTask = completing || localClosing;

  const dialog = (
    <>
      <div
        className={`fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center ${zIndexClass} p-4`}
        onClick={() => {
          if (closingTask) return;
          if (showReactionPicker) {
            setShowReactionPicker(false);
            return;
          }
          onClose();
        }}
      >
        <div
          className={`relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-[80vw] max-w-[80vw] max-h-[90vh] overflow-hidden flex flex-col border ${
            isUrgentTask
              ? 'border-red-300 dark:border-red-700/70'
              : 'border-gray-100 dark:border-gray-700'
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <WorkCloseProgressOverlay open={closingTask} variant="task" />
          <div className="flex-1 min-h-0 flex flex-col sm:flex-row">
            <div className="flex-1 min-w-0 overflow-y-auto px-5 py-4 space-y-5">
          <div className="flex items-center gap-1 border-b border-gray-200 dark:border-gray-700 -mx-1">
            {[
              { id: 'details', label: 'Details' },
              { id: 'completion', label: 'Post completion' },
              { id: 'history', label: 'History' },
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setPopupTab(tab.id)}
                className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
                  popupTab === tab.id
                    ? 'border-primary-500 text-primary-600 dark:text-primary-300'
                    : 'border-transparent text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
              >
                {tab.label}
              </button>
            ))}
            <span className="ml-auto mb-1 inline-flex items-center gap-2">
              <EnableLiveTrackingButton
                itemType="TASK"
                taskId={displayTask.id}
                sourceMessageId={displayTask.messageId}
                sourceChannelId={taskChannel?.id || displayTask.channelId}
                title={displayTask.title}
                assigneeUserId={delegateIds[0] || displayTask.createdByUserId}
                assigneeName={formatTaskAssigneeLabel(displayTask, users, user?.id)
                  || getUserDisplayName(delegateIds[0] || displayTask.createdByUserId)}
              />
              <span className="text-sm font-bold uppercase tracking-wide text-orange-600 dark:text-orange-300">
                Task
              </span>
              {done ? (
                <span className="text-xl sm:text-2xl font-black uppercase tracking-wide text-emerald-600 dark:text-emerald-400 leading-none">
                  Completed
                </span>
              ) : null}
            </span>
          </div>
          {popupTab === 'completion' ? (
            <PostCompletionEditor
              enabled={Boolean(displayTask.postCompletionEnabled) || (displayTask.postCompletionActions || []).length > 0}
              actions={displayTask.postCompletionActions || []}
              users={users || []}
              channels={(channels || []).filter((c) => {
                if (!c || c.systemChannelKey) return false;
                const type = String(c.type || '').toUpperCase();
                return type !== 'DIRECT_MESSAGE' && type !== 'DIRECT' && type !== 'DM';
              })}
              channelName={taskChannel?.name || ''}
              disabled={!canEditTask}
              title="Post completion"
              description="When this task is marked complete, these follow-ups run for the people you choose."
              onChange={handlePostCompletionChange}
            />
          ) : popupTab === 'history' ? (
            <WorkItemHistoryPanel
              entityLabel="task"
              endpoint={displayTask?.id ? `/tasks/${displayTask.id}/history` : ''}
              reloadToken={popupTab}
              fallback={displayTask?.createdAt ? {
                action: 'CREATED',
                summary: 'Created this task',
                actorName: getUserDisplayName(displayTask.createdByUserId) || 'Someone',
                createdAt: displayTask.createdAt,
              } : null}
            />
          ) : (
          <>
          <div className="flex items-start gap-3">
            <span className={`inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl ${
              done
                ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/35 dark:text-emerald-300'
                : isUrgentTask
                  ? 'bg-red-50 text-red-600 dark:bg-red-900/35 dark:text-red-300'
                  : 'bg-orange-50 text-orange-600 dark:bg-orange-900/30 dark:text-orange-300'
            }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {done ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                )}
                </svg>
            </span>
              <div className="flex-1 min-w-0">
              {canEditTask ? (
                <input
                  type="text"
                  value={titleDraft}
                  onChange={(event) => {
                    setTitleDraft(event.target.value);
                    setLiveTask((prev) => ({ ...(prev || displayTask), title: event.target.value }));
                  }}
                  className="w-full text-lg font-bold text-gray-900 dark:text-white leading-snug bg-transparent border border-transparent hover:border-gray-200 dark:hover:border-gray-600 focus:border-primary-400 rounded-lg px-1 -mx-1 py-0.5 focus:outline-none focus:ring-2 focus:ring-primary-500/25"
                  placeholder="Task title"
                />
              ) : (
                <h2 className="text-lg font-bold text-gray-900 dark:text-white leading-snug break-words">
                  {displayTask.title || getInitialWordsFromDescription(displayTask.description, 12)}
                </h2>
              )}
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${getStatusColor(displayTask.status)}`}>
                  {getStatusLabel(displayTask.status)}
                  </span>
                {(displayTask.tags || []).map((tag) => (
                  <span
                    key={tag}
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${
                      String(tag).trim().toLowerCase() === 'cloned'
                        ? 'bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-900/40 dark:text-violet-200 dark:border-violet-700'
                        : 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600'
                    }`}
                  >
                    {tag}
                  </span>
                ))}
                <button
                  type="button"
                  data-task-reaction-trigger
                  onClick={toggleReactionPicker}
                  className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-dashed border-gray-300 dark:border-gray-600 text-gray-500 hover:text-primary-600 hover:border-primary-400 dark:hover:text-primary-300"
                  title="Add icon"
                  aria-label="Add icon"
                  aria-expanded={showReactionPicker}
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </button>
                {canGoToLinkedChat && (
                  <button
                    type="button"
                    onClick={() => handleGoToMessage({
                      ...(displayTask || task),
                      messageId: linkedChatMessageId,
                      channelId: linkedChatChannelId,
                    })}
                    className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-[11px] font-semibold text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/30 border border-primary-200/80 dark:border-primary-800/60 hover:bg-primary-100 dark:hover:bg-primary-900/50 transition-colors"
                    title="Open source chat message"
                    aria-label="Open source chat message"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    Chat
                  </button>
                )}
                <TaskEstimateBadge minutes={totalMinutes} />
                {isUrgentTask && (
                  <UrgentCountdownBadge deadlineAt={displayTask.urgentDeadlineAt} compact blink={false} />
                )}
                {!done && namedSubtasks.length === 0 && (
                  <WorkItemTimer
                    targetType="TASK"
                    targetId={displayTask.id}
                    noun="task"
                    disabled={subtasksReadOnly || workTimerDisabled}
                  />
                  )}
                </div>
            </div>
          </div>

              {(canMapWorkflow || linkedWorkflows.length > 0 || loadingWorkflows) && (
                <section>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                    Workflows
                  </h3>
                  {loadingWorkflows && linkedWorkflows.length === 0 ? (
                    <p className="text-xs text-gray-500 dark:text-gray-400">Loading workflows…</p>
                  ) : (
                    <div className="space-y-2">
                      {linkedWorkflows.map((item) => {
                        const track = (item.workflowTrackId || '').trim();
                        const title = workflowItemLabel(item);
                        const typeId = item.workflowByCategory?.['work-item-type']
                          || getActiveWorkflowByCategory(item.reactions || [])['work-item-type'];
                        const typeDisplay = typeId ? getReactionDisplay(typeId) : null;
                        return (
                          <div key={item.messageId} className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => handleOpenWorkflow(item)}
                              className="flex-1 min-w-0 flex items-center gap-2 text-left rounded-xl px-3 py-2.5 bg-primary-50/80 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/40 border border-primary-100 dark:border-primary-800/50 transition-colors"
                              title={[typeDisplay?.label, track, title].filter(Boolean).join(' · ') || 'Open workflow'}
                            >
                              <span className="flex-1 min-w-0 flex items-center gap-2">
                                {typeDisplay ? (
                                  <span className="flex-shrink-0 inline-flex items-center gap-0.5 max-w-[8rem] min-h-[1.375rem] px-1.5 py-0.5 rounded-md text-[10px] font-semibold leading-tight truncate border border-violet-200/90 bg-violet-50 text-violet-800 dark:border-violet-700/60 dark:bg-violet-900/25 dark:text-violet-200">
                                    <span aria-hidden="true">{typeDisplay.emoji}</span>
                                    <span className="truncate">{typeDisplay.label}</span>
                      </span>
                                ) : null}
                                {track ? (
                                  <span className="flex-shrink-0 inline-flex items-center max-w-[9rem] min-h-[1.375rem] px-1.5 py-0.5 rounded-md text-[10px] font-semibold leading-tight truncate border border-indigo-200/90 bg-indigo-50 text-indigo-800 dark:border-indigo-700/60 dark:bg-indigo-900/25 dark:text-indigo-200">
                                    {track}
                        </span>
                      ) : null}
                                <span className="min-w-0 text-sm font-medium text-primary-700 dark:text-primary-300 truncate">
                                  {title}
                                </span>
                              </span>
                              <svg className="w-4 h-4 flex-shrink-0 text-primary-500 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </button>
                            {canMapWorkflow ? (
                              <button
                                type="button"
                                onClick={(event) => handleUnmapWorkflow(event, item.messageId)}
                                disabled={unmappingWorkflowId === item.messageId}
                                className="flex-shrink-0 p-2 rounded-lg text-gray-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 dark:hover:text-rose-400 disabled:opacity-50"
                                title="Unmap workflow"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {canMapWorkflow ? (
                    <button
                      type="button"
                      onClick={() => setWorkflowSearchOpen(true)}
                      className="mt-2 inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-semibold text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30"
                    >
                      <PlusIcon className="w-3.5 h-3.5" />
                      Map workflow
                    </button>
                  ) : null}
                </section>
              )}

              <section>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                  Assigned to
                </h3>
                {chatLinkedTask && canDelegate ? (
                  <div className="w-full max-w-sm">
                    <WorkflowAssigneeDropdown
                      hideLabel
                      users={chatAssignableUsers}
                      selectedUserId={assigneeId}
                      selectedUserName={
                        assigneeId && String(assigneeId) === String(user?.id)
                          ? `You (${assigneeName || 'me'})`
                          : (assigneeName || '')
                      }
                      isOpen={assignOpen}
                      searchQuery={assignQuery}
                      disabled={delegating}
                      onToggleOpen={(open) => {
                        if (delegating) return;
                        setAssignOpen(open);
                        if (!open) setAssignQuery('');
                      }}
                      onSearchChange={setAssignQuery}
                      onSelect={handleAssignChatTask}
                    />
                  </div>
                ) : (
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex-1 min-w-0">
                    {canDelegate ? (
                      <TaskDelegateDropdown
                        users={delegateUsers}
                        selectedIds={delegateIds}
                        selectedLabel={selectedDelegateLabel}
                        isOpen={delegateOpen}
                        searchQuery={delegateQuery}
                        disabled={delegating}
                        onToggleOpen={(open) => {
                          if (delegating) return;
                          setDelegateOpen(open);
                          if (!open) setDelegateQuery('');
                        }}
                        onSearchChange={setDelegateQuery}
                        onToggleUser={handleToggleDelegate}
                      />
                    ) : delegationInfo?.names?.length > 0 ? (
                      <p className="text-sm text-gray-700 dark:text-gray-300 truncate">
                        {delegationInfo.names.join(', ')}
                        {delegationInfo.suffix ? ` ${delegationInfo.suffix}` : ''}
                    </p>
                  ) : (
                      <p className="text-sm text-gray-400 dark:text-gray-500">Not assigned</p>
                    )}
                    </div>
              </div>
                )}
              </section>

              {displayTask?.reminderId ? (
              <section>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                  Reminder
                </h3>
                <button
                  type="button"
                  onClick={() => {
                    if (!users?.length) dispatch(fetchUsers());
                    setReminderOpen(true);
                  }}
                  className="w-full max-w-sm text-left rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/40 px-3 py-2.5 hover:border-primary-300 dark:hover:border-primary-700"
                >
                  <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                    {displayTask.reminderTitle || 'Task reminder'}
                  </p>
                  <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                    {[displayTask.reminderDate, displayTask.reminderTime].filter(Boolean).join(' · ') || 'Scheduled'}
                    {displayTask.reminderUserId
                      ? ` · ${sameUserId(displayTask.reminderUserId, user?.id) ? 'You' : (getUserDisplayName(displayTask.reminderUserId) || 'Assigned person')}`
                      : ''}
                  </p>
                  <span className="mt-1 inline-block text-[11px] font-semibold text-primary-600 dark:text-primary-400">
                    Edit reminder
                  </span>
                </button>
              </section>
              ) : null}

              <section>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                  Description
                </h3>
                <div className="flex items-start gap-2">
                  {canEditTask ? (
                    <div className="flex-1 min-w-0 space-y-2">
                      <CompactRichEditor
                        quillRef={descriptionQuillRef}
                        value={descriptionDraft}
                        onChange={(content) => {
                          setDescriptionDraft(content);
                          setLiveTask((prev) => ({ ...(prev || displayTask), description: content }));
                        }}
                        placeholder="Add a description"
                        minHeight={120}
                        maxHeight={220}
                        instanceClass="task-dialog-quill-desc"
                        channelMemberIds={commentChannelMemberIds}
                      />
                      {taskMessage?.attachments && renderTaskAttachments(taskMessage.attachments)}
            </div>
                  ) : (
                  <div className="flex-1 min-w-0 text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900/60 rounded-xl px-3 py-2.5">
                    {renderRichOrPlain(displayTask.description, 'No description provided.')}
                    {taskMessage?.attachments && renderTaskAttachments(taskMessage.attachments)}
                  </div>
                  )}
            </div>
              </section>

              <section>
                <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Subtasks
                  {namedSubtasks.length > 0 && (
                    <span className="ml-1.5 normal-case tracking-normal text-gray-400">
                      {namedSubtasks.length}
                    </span>
                  )}
                </h3>
                {canAddSubtask && !addingSubtask && (
                  <button
                    type="button"
                    onClick={() => setAddingSubtask(true)}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-semibold text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30"
                  >
                    <PlusIcon className="w-3.5 h-3.5" />
                    Add
                  </button>
                )}
              </div>
              <div className="rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-800/40">
                {loadingMappedTasks && namedSubtasks.length === 0 && !addingSubtask ? (
                  <p className="px-3 py-3 text-xs text-gray-500 dark:text-gray-400">Loading subtasks…</p>
                ) : namedSubtasks.length === 0 && !addingSubtask ? (
                  <p className="px-3 py-3 text-xs text-gray-400 dark:text-gray-500">No subtasks yet</p>
                ) : (
                  namedSubtasks.map((subtask) => (
                    <TaskSubtaskItem
                      key={subtask.id}
                      subtask={subtask}
                      indent={false}
                      busy={savingSubtask}
                      readOnly={subtasksReadOnly || !canEditTask}
                      onEdit={(payload) => handleEditSubtaskInternal(subtask, payload)}
                      onEditTime={canEditCompletionTime ? () => handleEditSubtaskTime(subtask) : undefined}
                      onDelete={canEditTask ? () => handleDeleteSubtaskInternal(subtask) : undefined}
                      onComplete={canEditTask ? () => handleCompleteSubtaskInternal(subtask) : undefined}
                      timerDisabled={subtasksReadOnly || workTimerDisabled}
                    />
                  ))
                )}
                {addingSubtask && (
                  <QuickTaskComposer
                    compact
                    flush
                    placeholder="Subtask title"
                    submitLabel="Add"
                    saving={savingSubtask}
                    onSubmit={async (payload) => {
                      await handleAddSubtaskInternal(payload);
                      setAddingSubtask(false);
                    }}
                    onCancel={() => setAddingSubtask(false)}
                  />
                )}
                      </div>
              </section>

              <section className="space-y-1.5">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Other info
                </h3>
                {task.messageId && loadingTaskMessage && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">Loading linked message…</p>
                )}
                {taskMessage && (
                  <div className="flex items-center gap-2">
                          <PresignedAvatar
                            fileKey={taskMessage.senderProfilePicture}
                            name={taskMessage.senderName}
                      className="w-7 h-7 rounded-full object-cover"
                      fallbackClassName="w-7 h-7 bg-primary-500 rounded-full flex items-center justify-center text-white text-[11px] font-medium"
                          />
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {taskMessage.senderName || 'Unknown User'}
                          </span>
                    {taskChannel && taskChannel.type !== 'DIRECT_MESSAGE' && taskChannel.type !== 'direct' && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        in <span className="font-medium text-primary-600 dark:text-primary-400">{taskChannel.name}</span>
                                </span>
                            )}
                        </div>
                )}
                {task.createdAt && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Created {formatRelativeTime(task.createdAt).toLowerCase()}
                  </p>
                )}
                {(task.completed || done) && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {task.completed ? `Completed ${formatDate(task.completed)}` : 'Completed'}
                    {formatEstimateTime(displayTask.completionTimeInMinutes)
                      ? ` · Finished in ${formatEstimateTime(displayTask.completionTimeInMinutes)}`
                      : ''}
                    {canEditCompletionTime ? (
                      <button
                        type="button"
                        onClick={handleEditCompletionTime}
                        className="ml-2 text-[11px] font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400"
                      >
                        Edit time
                      </button>
                    ) : null}
                  </p>
                )}
              </section>
          </>
          )}
            </div>

            <div className="w-full sm:w-[28rem] lg:w-[32rem] flex-shrink-0 sm:min-h-0 border-t sm:border-t-0 sm:border-l border-gray-100 dark:border-gray-700 flex flex-col min-h-[14rem] sm:max-h-full bg-gray-50/70 dark:bg-gray-900/25">
              <div className="flex items-start gap-2 px-4 py-4 border-b border-gray-100 dark:border-gray-700">
                <div className="flex-1 min-w-0">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                    Progress
                  </h3>
                  {displayTask.status !== TaskStatus.CANCELLED ? (
                    <TaskProgressControl
                      value={progressPct}
                      onChange={handleProgressChange}
                      disabled={!canEditTask}
                      collapsible={false}
                      />
                    ) : (
                    <p className="text-xs text-gray-400">Cancelled</p>
                    )}
                  </div>
                    <SpacePicker
                      variant="combo"
                      valueId={displaySpaceId}
                      valueName={displaySpaceName || (spaceLocked ? 'My space' : '')}
                      locked={spaceLocked}
                      disabled={!canEditTask && !spaceLocked}
                      onChange={handleSaveTaskSpace}
                    />
                    <button
                      type="button"
                  onClick={onClose}
                  className="flex-shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-white dark:hover:text-gray-200 dark:hover:bg-gray-700 transition-colors"
                  aria-label="Close"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
              </div>


              <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
                <div className="flex items-center justify-between px-4 pt-3 pb-1 flex-shrink-0">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Reply thread
                  </h3>
                  {chatTimeline.length > 0 ? (
                    <span className="text-[10px] text-gray-400 dark:text-gray-500 tabular-nums">
                      {chatTimeline.length}
                    </span>
                  ) : null}
                    </div>
                <div
                  ref={commentsScrollRef}
                  className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 py-2"
                  onScroll={(event) => {
                    const el = event.currentTarget;
                    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
                    shouldStickToLatestComment.current = remaining < 48;
                  }}
                >
                  <div className="space-y-3 min-w-0">
                    {!commentRootMessageId && chatTimeline.length === 0 ? (
                      <p className="text-xs text-gray-400 dark:text-gray-500">
                        Comments will be available once this task is saved.
                      </p>
                    ) : loadingComments && chatTimeline.length === 0 ? (
                      <p className="text-xs text-gray-400 dark:text-gray-500">Loading…</p>
                    ) : chatTimeline.length === 0 ? (
                      <p className="text-xs text-gray-400 dark:text-gray-500">No chats or comments yet</p>
                    ) : (
                      chatTimeline.map((item) => {
                        if (item.type === 'message') {
                          const message = item.message;
                          return (
                            <ChatMessageBubblePreview
                              key={item.id}
                              contentHtml={message.content}
                              attachments={getMessageDisplayAttachments(message)}
                              attachmentGroupName={message.attachmentGroupName}
                              linkPreview={message.linkPreview}
                              reactions={message.reactions}
                              workflowTitle={message.workflowTitle}
                              senderName={message.senderName || getUserDisplayName(message.senderId) || 'User'}
                              senderProfilePicture={message.senderProfilePicture}
                              createdAt={message.createdAt || message.timestamp}
                              isOwn={String(message.senderId) === String(user?.id)}
                              isEdited={Boolean(message.isEdited || message.edited)}
                              alignLeft
                              compact
                              onViewImage={(images, index) => handleViewImage(images, index, {
                                messageId: message.id,
                                channelId: message.channelId || commentChannelId,
                                replyToMessageId: message.replyToMessageId || commentRootMessageId,
                              })}
                              onDownload={handleCommentDownload}
                              onSaveEdit={item.isComment && String(message.senderId) === String(user?.id)
                                ? (html) => handleEditComment(message, html)
                                : undefined}
                              onDelete={item.isComment && String(message.senderId) === String(user?.id)
                                ? () => handleDeleteComment(message)
                                : undefined}
                              onNavigate={
                                item.navigateTask?.messageId && onNavigateToMessage
                                  ? () => handleGoToMessage(item.navigateTask)
                                  : undefined
                              }
                            />
                          );
                        }

                        const mappedTask = item.task;
                        const description = mappedTask.description || '';
                        const isHtml = looksLikeHtml(description);
                        const richHtml = isHtml ? normalizeDescriptionHtml(description) : '';
                        return (
                          <ChatMessageBubblePreview
                            key={item.id}
                            contentHtml={isHtml ? richHtml : undefined}
                            content={!isHtml ? (description || 'No message content.') : undefined}
                            senderName={getUserDisplayName(mappedTask.createdByUserId) || 'User'}
                            senderProfilePicture={null}
                            createdAt={mappedTask.createdAt}
                            alignLeft
                            compact
                            onViewImage={handleViewImage}
                            onDownload={handleCommentDownload}
                            onNavigate={
                              item.navigateTask?.messageId && onNavigateToMessage
                                ? () => handleGoToMessage(item.navigateTask)
                                : undefined
                            }
                          />
                        );
                      })
                    )}
                    <div ref={commentsEndRef} />
                  </div>
                </div>
                <div className="flex-shrink-0 border-t border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
                  {canPostComment ? (
                    <MessageInput
                      key={`task-comment-${commentChannelId}-${commentRootMessageId}`}
                      onSendMessage={handleAddComment}
                      channelId={commentChannelId}
                      replyToMessage={{ id: commentRootMessageId }}
                      isThreadInput
                      autoFocusComposer={false}
                    />
                  ) : (
                    <div className="px-4 py-3">
                      <p className="text-[10px] text-gray-400 dark:text-gray-500 leading-snug">
                        {displayTask?.id
                          ? 'Loading comments…'
                          : 'Save this task to enable comments and attachments.'}
                      </p>
                    </div>
                  )}
                </div>
                    </div>
            </div>
              </div>

          <div className="flex items-center gap-2 px-5 py-3 border-t border-gray-100 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/30">
            {done && onUndoComplete && (
              <button
                type="button"
                onClick={onUndoComplete}
                disabled={completing}
                className="inline-flex items-center gap-1 text-[12px] font-semibold text-amber-700 hover:text-amber-800 dark:text-amber-300 disabled:opacity-50"
                title="Undo completion and revert time spent"
              >
                {completing ? 'Undoing…' : 'Undo'}
              </button>
            )}
            {done && canEditCompletionTime && (
              <button
                type="button"
                onClick={handleEditCompletionTime}
                className="inline-flex items-center gap-1 text-[12px] font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400"
              >
                Edit time
              </button>
            )}
            {canEditTask && (
              <button
                type="button"
                onClick={handleCompleteTaskInternal}
                disabled={completing || localClosing || !canCompleteParent}
                className="inline-flex items-center gap-1 text-[12px] font-semibold text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 disabled:opacity-50"
                title={!canCompleteParent ? unfinishedSubtasksMessage(unfinishedSubtasks) : undefined}
              >
                <CompleteTaskIcon className="w-3.5 h-3.5" />
                {completing || localClosing ? 'Completing…' : 'Complete task'}
              </button>
            )}
            {canEditTask && (
              <>
                <span className="text-[12px] font-semibold text-gray-300 dark:text-gray-600" aria-hidden="true">|</span>
                {isUrgentTask ? (
                  <button
                    type="button"
                    onClick={handleClearTaskUrgent}
                    disabled={markingUrgent}
                    className="inline-flex items-center gap-1 text-[12px] font-semibold text-red-600 hover:text-red-700 dark:text-red-400 disabled:opacity-50"
                  >
                    <span aria-hidden="true">🚨</span>
                    {markingUrgent ? 'Updating…' : 'Clear urgent'}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleMarkTaskUrgent}
                    disabled={markingUrgent}
                    className="inline-flex items-center gap-1 text-[12px] font-semibold text-red-600 hover:text-red-700 dark:text-red-400 disabled:opacity-50"
                  >
                    <span aria-hidden="true">🚨</span>
                    {markingUrgent ? 'Updating…' : 'Make urgent'}
                  </button>
                )}
              </>
            )}
            {canEditTask && (
              <>
                <span className="text-[12px] font-semibold text-gray-300 dark:text-gray-600" aria-hidden="true">|</span>
                <button
                  type="button"
                  onClick={() => {
                    if (!users?.length) dispatch(fetchUsers());
                    setReminderOpen(true);
                  }}
                  className="inline-flex items-center gap-1 text-[12px] font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400"
                >
                  {displayTask?.reminderId ? 'Edit reminder' : 'Add reminder'}
                </button>
              </>
            )}
            {canEditTask && canDeleteTask && (
              <span className="text-[12px] font-semibold text-gray-300 dark:text-gray-600" aria-hidden="true">|</span>
            )}
            {canDeleteTask && (
              <button
                type="button"
                onClick={handleDeleteTaskInternal}
                disabled={deletingTask}
                className="inline-flex items-center gap-1 text-[12px] font-semibold text-rose-600 hover:text-rose-700 dark:text-rose-400 disabled:opacity-50"
              >
                <DeleteTaskIcon className="w-3.5 h-3.5" />
                {deletingTask ? 'Deleting…' : 'Delete Task'}
              </button>
            )}
            <div className="flex-1" />
            {displayTask?.id ? (
              <button
                type="button"
                onClick={() => setShareOpen(true)}
                className="px-3 py-1.5 text-[12px] font-semibold rounded-lg text-primary-700 dark:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/30"
              >
                Share
              </button>
            ) : null}
            {footerActions || (
              <Link
                to="/chat/tasks"
                className="px-3 py-1.5 text-[12px] font-semibold rounded-lg text-primary-700 dark:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/30"
                onClick={onClose}
              >
                Task Management
              </Link>
            )}
            {canEditTask && (
              <button
                type="button"
                onClick={handleSaveTask}
                disabled={savingTask || !displayTask?.id}
                className="px-3 py-1.5 text-[12px] font-semibold rounded-lg text-white bg-primary-600 hover:bg-primary-500 disabled:opacity-50 border border-primary-600"
              >
                {savingTask ? 'Saving…' : 'Save'}
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-[12px] font-semibold rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {viewingImage && (
        <ImageViewerModal
          images={viewingImage.images}
          initialIndex={viewingImage.index}
          onClose={() => setViewingImage(null)}
          onDownload={handleCommentDownload}
          onAttachmentMessageUpdated={handleAttachmentMessageUpdated}
        />
      )}
      {workflowSearchOpen && (
        <WorkflowSearchDialog
          isOpen
          onClose={() => setWorkflowSearchOpen(false)}
          onWorkflowSelected={handleMapSelectedWorkflow}
          excludeMessageIds={taskWorkflowMessageIds(displayTask)}
          mapping={mappingWorkflow}
        />
      )}
      {showReactionPicker && reactionPickerPosition && ReactDOM.createPortal(
        <ReactionPicker
          initialTab="workflow"
          hideCategories={['work-item-type']}
          onSelect={handleReactionClick}
          currentReactions={resolvedTaskReactions}
          currentUserId={currentUserId}
          style={{
            position: 'fixed',
            top: reactionPickerPosition.top,
            left: reactionPickerPosition.left,
            zIndex: REACTION_PICKER_Z_INDEX,
          }}
        />,
        document.body,
      )}
      {completionTimeDialog}
      {urgentDeadlineDialog}
      {shareOpen && displayTask?.id ? (
        <WorkItemShareMenu
          share={{
            kind: WORK_ITEM_SHARE_KIND.TASK,
            itemId: displayTask.id,
            title: (() => {
              const named = (displayTask.title || '').trim();
              if (named) return named;
              if (!displayTask.description) return 'Task';
              const snippet = getInitialWordsFromDescription(displayTask.description, 12);
              return snippet && snippet !== 'No description' ? snippet : 'Task';
            })(),
            channelId: displayTask.channelId || taskChannel?.id || '',
          }}
          onClose={() => setShareOpen(false)}
        />
      ) : null}
      {reminderOpen && displayTask?.id ? (
        <TaskReminderModal
          isOpen
          task={displayTask}
          onClose={() => setReminderOpen(false)}
          onSaved={(updated, mode) => {
            if (updated) setLiveTask(updated);
            onDelegated?.(updated);
            dispatch(addNotification({
              type: 'success',
              message: mode === 'moved'
                ? 'Reminder moved to the selected person'
                : mode === 'updated'
                ? 'Reminder updated'
                : 'Reminder created',
            }));
          }}
        />
      ) : null}
    </>
  );

  if (usePortal && typeof document !== 'undefined') {
    return ReactDOM.createPortal(dialog, document.body);
  }

  return dialog;
};

export default TaskViewDialog;
